// The historic price archive, from the website's side.
//
// WHAT CHANGED
// Every page in this app shows a change over time, and until now all of them
// could only measure from the day the row was created — because row.priceHistory
// starts then. Lend something in October, open the Lending page, and "since
// lent" read n/a forever. Buy something in 2024 and there was no way to say
// whether the market had moved under it.
//
// /api/history serves a read-only CSFloat archive going back to 2023-10-02, so
// any date the app knows about — lent on, bought on, sold on, traded on, added
// to the wishlist on — can now be priced. This module is the only thing that
// talks to it.
//
// HOW IT IS SHAPED
// A module-level store rather than a context provider, deliberately: the
// question "what was this worth on that day" gets asked from a dozen components
// that do not share a parent worth threading a provider through, and the answer
// is global — it does not depend on who is asking. Components subscribe with a
// hook; the store batches every ask made in the same tick into one POST.
//
// WHICH MARKET
// The archive is CSFloat. So is `csfloatPrices` in the app. Every comparison
// here is CSFloat-then against CSFloat-now, never against the Buff163 figure
// the cards headline — Buff and CSFloat sit several percent apart on the same
// item, and mixing them turns that spread into fake drift. That is what
// nowPrice() below is for.

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { API } from '../api';
import { CONDITION_MAP } from './helpers';

/* ── Keys ─────────────────────────────────────────────────── */

/** The archive keys on market_hash_name, which is what the CSFloat map uses. */
export function archiveName(skin, condition) {
  if (!skin) return null;
  if (!condition) return skin;
  return `${skin} (${CONDITION_MAP[condition] || condition})`;
}

/** Same thing straight off a tracker row. */
export function rowName(row) {
  return archiveName(row?.skin?.value, row?.condition?.value);
}

/** ISO day out of anything the rows store — timestamps, dates, nulls. */
export function dayOf(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const t = new Date(value);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
}

const keyOf = (name, date) => `${name}@@${date}`;

/* ── Store ────────────────────────────────────────────────── */

const readings  = new Map();   // "name@@date" -> reading | null (null = asked, nothing there)
const asked     = new Set();   // everything already requested, so a miss is asked once
let   queue     = new Map();   // "name@@date" -> { name, date } waiting for the next flush
let   flushTimer = null;
let   version   = 0;
const listeners = new Set();

// undefined until /meta answers, then the object, or null when the server has
// no archive. Components read `available` to decide whether to show any of this
// at all — with no archive file the app has to look exactly like it did before.
let meta = undefined;
let metaPromise = null;

function emit() {
  version += 1;
  listeners.forEach(fn => fn());
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const snapshot = () => version;

/** One /meta fetch per page load, shared by every hook that wants it. */
function ensureMeta() {
  if (meta !== undefined || metaPromise) return metaPromise;
  metaPromise = fetch(`${API}/api/history/meta`)
    .then(r => r.json())
    .then(m => { meta = m?.available ? m : null; emit(); })
    .catch(() => { meta = null; emit(); });
  return metaPromise;
}

const MAX_BATCH = 400;   // matches the server's cap

async function flush() {
  flushTimer = null;
  const batch = [...queue.values()];
  queue = new Map();
  if (!batch.length) return;

  await ensureMeta();
  if (meta === null) {
    // No archive on this server. Mark everything answered-and-empty so the
    // asks stop, rather than retrying a 503 for every card on every render.
    batch.forEach(p => readings.set(keyOf(p.name, p.date), null));
    emit();
    return;
  }

  for (let i = 0; i < batch.length; i += MAX_BATCH) {
    const chunk = batch.slice(i, i + MAX_BATCH);
    try {
      const res = await fetch(`${API}/api/history/at`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ points: chunk }),
      });
      const json = await res.json();
      const results = Array.isArray(json?.results) ? json.results : [];
      chunk.forEach((p, n) => readings.set(keyOf(p.name, p.date), results[n] || null));
    } catch {
      // A failed chunk is left OUT of `readings` but stays in `asked`, so the
      // UI shows "no reading" instead of spinning — and a later mount with a
      // different point set will not re-request these forever.
      chunk.forEach(p => readings.set(keyOf(p.name, p.date), null));
    }
  }
  emit();
}

/** Queue anything not already known, and schedule the one request. */
function request(points) {
  let added = false;
  for (const p of points) {
    if (!p?.name || !p?.date) continue;
    const k = keyOf(p.name, p.date);
    if (asked.has(k)) continue;
    asked.add(k);
    queue.set(k, { name: p.name, date: p.date });
    added = true;
  }
  // 30ms is long enough for every card in a page to mount and short enough
  // that nobody sees the gap.
  if (added && !flushTimer) flushTimer = setTimeout(flush, 30);
}

/* ── Hooks ────────────────────────────────────────────────── */

/**
 * Prices for a set of (name, date) pairs.
 *
 * `points` is re-derived on every render by its caller, so this keys on the
 * contents rather than the array identity — otherwise it would re-request the
 * whole page every time a filter chip moved.
 *
 * Returns a getter rather than a keyed object because callers already hold the
 * name and date they asked about; handing back `at(name, date)` keeps the call
 * site readable and avoids building a second index of the same data.
 */
export function useArchivePrices(points) {
  const v = useSyncExternalStore(subscribe, snapshot, snapshot);

  const sig = useMemo(
    () => (points || []).filter(p => p?.name && p?.date).map(p => keyOf(p.name, p.date)).sort().join('|'),
    [points],
  );

  useEffect(() => {
    ensureMeta();
    if (sig) request(sig.split('|').map(k => {
      const [name, date] = k.split('@@');
      return { name, date };
    }));
  }, [sig]);

  const at = useCallback((name, date) => {
    if (!name || !date) return null;
    return readings.get(keyOf(name, date)) ?? null;
  }, [v]);   // eslint-disable-line react-hooks/exhaustive-deps -- v is the store subscription

  const settled = useCallback((name, date) => {
    if (!name || !date) return true;
    return readings.has(keyOf(name, date));
  }, [v]);   // eslint-disable-line react-hooks/exhaustive-deps

  return {
    at, settled,
    available: meta !== undefined && meta !== null,
    loading: meta === undefined,
    meta: meta || null,
  };
}

/** One pair, for the components that only ever need one. */
export function useArchivePrice(name, date) {
  const points = useMemo(() => (name && date ? [{ name, date }] : []), [name, date]);
  const { at, available, meta: m } = useArchivePrices(points);
  return { reading: at(name, date), available, meta: m };
}

/**
 * The whole recorded line for one skin.
 *
 * Used by the detail modal, which until now could only draw the days since the
 * row existed. `enabled` keeps it from firing until the modal is actually open.
 */
const seriesCache = new Map();   // name -> { points, first, last, total }

export function useArchiveSeries(name, { enabled = true, from = null } = {}) {
  const [, bump] = useState(0);
  const cacheKey = `${name}|${from || ''}`;

  useEffect(() => {
    if (!enabled || !name || seriesCache.has(cacheKey)) return;
    // Claimed before the fetch starts: `has` is what guards re-entry, so two
    // components opening the same skin at once must not both request it.
    seriesCache.set(cacheKey, undefined);
    let live = true;
    ensureMeta().then(() => {
      if (meta === null) { seriesCache.set(cacheKey, null); if (live) bump(n => n + 1); return; }
      const qs = new URLSearchParams({ name });
      if (from) qs.set('from', from);
      fetch(`${API}/api/history/series?${qs}`)
        .then(r => (r.ok ? r.json() : null))
        .then(j => { seriesCache.set(cacheKey, j); if (live) bump(n => n + 1); })
        .catch(() => { seriesCache.set(cacheKey, null); if (live) bump(n => n + 1); });
    });
    return () => { live = false; };
  }, [name, cacheKey, enabled, from]);

  const data = seriesCache.get(cacheKey);
  return {
    series: data?.points || null,
    first: data?.first || null,
    last: data?.last || null,
    total: data?.total || 0,
    // The day the series stops being reconstructed sale medians and becomes
    // real listing snapshots. Charts mark it; a line that changes meaning
    // halfway along has to say so.
    eraFrom: data?.eraFrom || null,
    loading: enabled && !!name && data === undefined,
    available: meta !== undefined && meta !== null,
  };
}

/**
 * The chain-linked market index, and optionally how far it has moved since a
 * date — the benchmark a position's own move gets read against.
 */
const indexCache = new Map();   // since|'' -> payload

export function useMarketIndex(since = null) {
  const [, bump] = useState(0);
  const key = since || '';

  useEffect(() => {
    if (indexCache.has(key)) return;
    let live = true;
    indexCache.set(key, undefined);
    ensureMeta().then(() => {
      if (meta === null) { indexCache.set(key, null); if (live) bump(n => n + 1); return; }
      fetch(`${API}/api/history/index${since ? `?since=${encodeURIComponent(since)}` : ''}`)
        .then(r => (r.ok ? r.json() : null))
        .then(j => { indexCache.set(key, j); if (live) bump(n => n + 1); })
        .catch(() => { indexCache.set(key, null); if (live) bump(n => n + 1); });
    });
    return () => { live = false; };
  }, [key, since]);

  const data = indexCache.get(key);
  return {
    points: data?.points || null,
    since: data?.since || null,
    loading: data === undefined,
    available: meta !== undefined && meta !== null,
  };
}

/**
 * "How much has this moved since <the day it happened>", for a whole page.
 *
 * This is the hook nearly every tab actually wants, and it exists so that the
 * three fiddly parts are written once instead of six times:
 *
 *   1. it asks for a SECOND reading dated today, so `now` can come from the
 *      archive when the live CSFloat feed has never heard of the item;
 *   2. it takes `now` from CSFloat, never from the Buff163 figure on the card,
 *      so the number is drift and not a cross-market spread;
 *   3. it lets an entry name its own end date (`to`) — a loan that came back in
 *      June should be measured to June, not to today.
 *
 * `entries` are { key, name, date, to? }. Callers should useMemo them; the
 * signature guard below makes an unmemoized array merely wasteful rather than
 * an infinite render loop.
 */
export function useSinceMoves(entries, csfloatPrices) {
  const today = new Date().toISOString().slice(0, 10);

  const sig = useMemo(
    () => (entries || []).filter(e => e?.name && e?.date)
      .map(e => `${e.key}~${e.name}~${e.date}~${e.to || ''}`).join('|'),
    [entries],
  );

  const points = useMemo(() => {
    const out = [];
    const seen = new Set();
    const push = (name, date) => {
      const k = keyOf(name, date);
      if (!seen.has(k)) { seen.add(k); out.push({ name, date }); }
    };
    for (const e of entries || []) {
      if (!e?.name || !e?.date) continue;
      push(e.name, e.date);
      push(e.name, e.to || today);
    }
    return out;
  }, [sig]);   // eslint-disable-line react-hooks/exhaustive-deps -- sig covers entries

  const { at, settled, available, loading, meta: m } = useArchivePrices(points);

  const moves = useMemo(() => {
    const map = new Map();
    for (const e of entries || []) {
      if (!e?.name || !e?.date) continue;
      const then = at(e.name, e.date);
      if (!then) continue;

      const endRead = at(e.name, e.to || today);
      const now = e.to
        // A closed interval is measured archive-to-archive at both ends: the
        // live feed knows nothing about what June looked like.
        ? (endRead ? { price: endRead.price, source: 'archive' } : null)
        : nowPrice(csfloatPrices, e.name, endRead?.price);
      const mv = moveSince(then, now?.price);
      if (mv) map.set(e.key, { ...mv, nowSource: now.source, nowDate: e.to ? endRead?.date : null });
    }
    return map;
  }, [sig, at, csfloatPrices]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Memoized as a whole: callers put this object in useMemo dependency lists
  // for their page totals, and a fresh object every render would defeat every
  // one of them.
  return useMemo(() => ({
    moves,
    get: key => moves.get(key) || null,
    available, loading, meta: m,
    // True once every point has an answer, so a page can hold a total back
    // rather than animating it upward as readings trickle in.
    settledAll: (entries || []).every(e => !e?.name || !e?.date
      || (settled(e.name, e.date) && settled(e.name, e.to || today))),
  }), [moves, available, loading, m, settled, sig]);   // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Your holdings against the market, over the same stretch of time.
 *
 * "+18% since I bought" means nothing on its own — the honest question is
 * whether that beat simply owning CS2 skins over the same months. The server's
 * chain-linked index answers the second half, and the trick is making the two
 * comparable: the benchmark is computed PER ITEM from that item's own purchase
 * date and weighted by what that item was worth then, so both sides cover the
 * identical set of holdings over the identical set of periods. A single "market
 * since my first purchase" figure would compare a portfolio built over two
 * years against a benchmark measured from its oldest day.
 *
 * Items the archive cannot price are dropped from BOTH sides.
 */
export function usePortfolioVsMarket(entries, csfloatPrices) {
  const { moves, available, loading } = useSinceMoves(entries, csfloatPrices);
  const { points: idx } = useMarketIndex();

  return useMemo(() => {
    if (!idx?.length || !moves.size) return null;

    // Nearest index step at or before a date. The index is monthly before
    // 2026-05 and daily after, which is exactly as precise as the underlying
    // data — no interpolation pretends otherwise.
    const levelAt = (iso) => {
      let out = null;
      for (const p of idx) {
        if (p.date <= iso) out = p; else break;
      }
      return (out || idx[0]).value;
    };
    const nowLevel = idx[idx.length - 1].value;

    let then = 0, now = 0, benchmark = 0, n = 0;
    for (const e of entries || []) {
      const mv = moves.get(e?.key);
      if (!mv) continue;
      const level = levelAt(e.date);
      if (!(level > 0)) continue;
      then += mv.then;
      now  += mv.now;
      // What this item would be worth today if it had simply tracked the market
      // from the day you bought it.
      benchmark += mv.then * (nowLevel / level);
      n += 1;
    }
    if (!n || !(then > 0)) return null;

    return {
      n, then, now,
      yourPct:   (now - then) / then * 100,
      marketPct: (benchmark - then) / then * 100,
      benchmark,
      available, loading,
    };
  }, [entries, moves, idx, available, loading]);
}

/* ── Reading the answers ──────────────────────────────────── */

/**
 * The CSFloat price now, from the same feed the archive was built from.
 *
 * The cards headline Buff163, but comparing a Buff price today against a
 * CSFloat price in October would report the gap between two marketplaces as if
 * the item had moved. So "now" comes from the live CSFloat map when it has the
 * item, and from the archive's own last reading when it does not.
 */
export function nowPrice(csfloatPrices, name, latestFallback = null) {
  const live = name ? csfloatPrices?.[name]?.price : null;
  if (live != null && live > 0) return { price: live, source: 'live' };
  if (latestFallback != null && latestFallback > 0) return { price: latestFallback, source: 'archive' };
  return null;
}

/**
 * then → now as one object, or null when either end is missing.
 *
 * Nothing is invented: no reading means no figure. A made-up baseline turns
 * ordinary market noise into a number that looks like it means something, which
 * is the failure mode this whole feature exists to avoid.
 */
export function moveSince(reading, now) {
  const then = reading?.price;
  if (!(then > 0) || !(now > 0)) return null;
  const change = now - then;
  return {
    then, now, change,
    pct: (change / then) * 100,
    // Carried through so the caller can caption itself: a reading 23 days from
    // the date asked for must not be printed as "the price that day".
    date: reading.date,
    requested: reading.requested,
    daysOff: reading.daysOff,
    exact: reading.exact,
    era: reading.era,
    direction: reading.direction,
  };
}

const SHORT = d => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'n/a');

/**
 * How a reading should describe itself, in one short line.
 *
 * Three honesty levels, because the archive has three kinds of answer:
 *   exact snapshot   "CSFloat close on Jun 4, 2026"
 *   exact backfill   "median sale on Jun 4, 2025"
 *   nearest reading  "nearest reading, 23 days later"
 */
export function readingNote(reading) {
  if (!reading) return 'no reading near that date';
  const what = reading.era === 'snapshot' ? 'CSFloat price' : 'median sale price';
  if (reading.exact) return `${what} on ${SHORT(reading.date)}`;
  const when = reading.direction === 'before' ? 'earlier' : 'later';
  return `nearest reading: ${SHORT(reading.date)}, ${reading.daysOff}d ${when}`;
}

/** A reading close enough to the date asked for to speak plainly about it. */
export function isTight(reading, days = 7) {
  return !!reading && reading.daysOff <= days;
}

export const fmtSigned = n => `${n >= 0 ? '+' : '-'}$${Math.abs(n).toFixed(2)}`;
export const fmtPct    = n => `${n >= 0 ? '+' : '-'}${Math.abs(n).toFixed(1)}%`;
